<template>
  <DCard class="site-card border-0 shadow-sm h-100">
    <template #header>
      <div class="d-flex justify-content-between align-items-center bg-white">
        <div class="d-flex align-items-center gap-2 header-title">
          <h5 class="mb-0 text-truncate site-name">{{ site.name }}</h5>
          <span class="status-badge rounded-pill flex-shrink-0" :class="statusBadge.cls">
            <span class="status-badge-dot" />
            {{ statusBadge.label }}
          </span>
        </div>
        <Link
          :href="`/sites/${site.id}/edit`"
          class="btn btn-sm btn-link text-muted p-0 flex-shrink-0"
          aria-label="Edit site"
        >
          <i-bi-pencil style="width: 16px; height: 16px" />
        </Link>
      </div>
    </template>

    <div v-if="check" class="d-flex flex-column gap-2">
      <div v-if="site.editor_url" class="probe-row small">
        <span class="status-dot" :class="check.editor_ok ? 'bg-success' : 'bg-danger'" />
        <span class="fw-semibold">Editor</span>
        <span class="text-muted metric">
          {{ probeSummary(check.editor_ok, check.editor_status, check.editor_latency_ms) }}
        </span>
        <span class="text-muted metric text-end">
          <template v-if="certDays(check.editor_cert_expires_at) !== null">
            cert {{ certDays(check.editor_cert_expires_at) }}d
          </template>
        </span>
      </div>

      <div v-if="site.public_url" class="probe-row small">
        <span class="status-dot" :class="check.public_ok ? 'bg-success' : 'bg-danger'" />
        <span class="fw-semibold">Public</span>
        <span class="text-muted metric">
          {{ probeSummary(check.public_ok, check.public_status, check.public_latency_ms) }}
        </span>
        <span class="text-muted metric text-end">
          <template v-if="certDays(check.public_cert_expires_at) !== null">
            cert {{ certDays(check.public_cert_expires_at) }}d
          </template>
        </span>
      </div>

      <div v-if="check.error" class="small text-danger">
        {{ check.error }}
      </div>

      <div class="d-flex align-items-center pt-1">
        <div
          class="history-strip d-flex align-items-center me-2"
          :title="`Last ${HISTORY_SLOTS} checks, oldest first`"
        >
          <span
            v-for="(slot, index) in historySlots"
            :key="index"
            class="history-dot"
            :class="slot ? historyDotClass(slot.status) : 'history-dot-empty'"
            :title="slot ? `${statusLabel(slot.status)} · ${slot.checked_at}` : undefined"
          />
        </div>
        <small class="text-muted ms-auto text-nowrap d-flex align-items-center gap-2">
          {{ checkedAgo }}
          <button
            type="button"
            class="btn btn-link p-0 refresh-btn"
            :disabled="checking"
            :title="checking ? 'Checking…' : 'Check now'"
            aria-label="Check now"
            @click="checkNow"
          >
            <i-bi-arrow-repeat :class="{ spinning: checking }" style="width: 16px; height: 16px" />
          </button>
        </small>
      </div>
    </div>
    <div v-else class="d-flex align-items-center text-muted small">
      Not checked yet.
      <button
        type="button"
        class="btn btn-link p-0 refresh-btn ms-auto"
        :disabled="checking"
        title="Check now"
        aria-label="Check now"
        @click="checkNow"
      >
        <i-bi-arrow-repeat :class="{ spinning: checking }" style="width: 16px; height: 16px" />
      </button>
    </div>

    <template #footer>
      <div class="footer-actions d-flex flex-wrap gap-2 align-items-center bg-white">
        <a
          v-if="site.editor_url"
          :href="site.editor_url"
          target="_blank"
          rel="noopener"
          class="btn btn-sm btn-outline-secondary"
        >
          <i-bi-box-arrow-up-right class="me-1" style="width: 12px; height: 12px" />
          Editor
        </a>
        <a
          v-if="site.public_url"
          :href="site.public_url"
          target="_blank"
          rel="noopener"
          class="btn btn-sm btn-outline-secondary"
        >
          <i-bi-globe2 class="me-1" style="width: 12px; height: 12px" />
          Live site
        </a>
        <a
          v-if="site.github_url"
          :href="site.github_url"
          target="_blank"
          rel="noopener"
          class="btn btn-sm btn-outline-secondary"
        >
          <i-bi-github class="me-1" style="width: 12px; height: 12px" />
          GitHub
        </a>
        <a
          v-if="site.netlify_admin_url"
          :href="site.netlify_admin_url"
          target="_blank"
          rel="noopener"
          class="btn btn-sm btn-outline-secondary"
        >
          <i-bi-cloud class="me-1" style="width: 12px; height: 12px" />
          Netlify
        </a>
      </div>
    </template>
  </DCard>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { Link, router } from "@inertiajs/vue3";
import { DCard } from "@omnitend/dashboard-for-laravel";
import type { Site, SiteCheck } from "../types";

const props = defineProps<{
  site: Site;
}>();

const HISTORY_SLOTS = 12;

const checking = ref(false);

const check = computed<SiteCheck | null>(() => props.site.latest_check ?? null);

// Controller sends the last 12 checks newest-first; render oldest -> newest,
// left-padded with empty slots so the strip always reads as a fixed window.
const historySlots = computed<(SiteCheck | null)[]>(() => {
  const recent = [...(props.site.checks ?? [])].reverse().slice(-HISTORY_SLOTS);
  return [...Array(HISTORY_SLOTS - recent.length).fill(null), ...recent];
});

const statusLabel = (status: string | null | undefined): string => {
  switch (status) {
    case "green":
      return "Healthy";
    case "amber":
      return "Warning";
    case "red":
      return "Down";
    default:
      return "Unchecked";
  }
};

const statusBadge = computed(() => {
  const status = props.site.last_status;
  const cls =
    status === "green"
      ? "status-badge-green"
      : status === "amber"
        ? "status-badge-amber"
        : status === "red"
          ? "status-badge-red"
          : "status-badge-gray";
  return { label: statusLabel(status), cls };
});

const historyDotClass = (status: string) => {
  switch (status) {
    case "green":
      return "bg-success";
    case "amber":
      return "bg-warning";
    case "red":
      return "bg-danger";
    default:
      return "bg-secondary";
  }
};

const probeSummary = (
  ok: boolean,
  status: number | null,
  latencyMs: number | null,
): string => {
  if (!ok && status === null) {
    return "unreachable";
  }

  const parts = [`HTTP ${status}`];
  if (latencyMs !== null) {
    parts.push(`${latencyMs}ms`);
  }
  return parts.join(" · ");
};

const certDays = (expiresAt: string | null): number | null => {
  if (!expiresAt) {
    return null;
  }

  const msRemaining = new Date(expiresAt).getTime() - Date.now();
  return Math.floor(msRemaining / (1000 * 60 * 60 * 24));
};

const checkedAgo = computed(() => {
  if (!check.value) {
    return "";
  }

  const minutes = Math.round(
    (Date.now() - new Date(check.value.checked_at).getTime()) / 60000,
  );

  if (minutes < 1) {
    return "just now";
  }
  if (minutes < 60) {
    return `${minutes} min ago`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }

  return `${Math.floor(hours / 24)}d ago`;
});

const checkNow = () => {
  checking.value = true;
  router.post(
    `/sites/${props.site.id}/check`,
    {},
    {
      preserveScroll: true,
      onFinish: () => {
        checking.value = false;
      },
    },
  );
};
</script>

<style scoped>
.site-card {
  transition: box-shadow 0.15s ease;
}

/* Flex children default to min-width:auto, so text-truncate on the name
   can't shrink it and long names push the badge out of the card. */
.header-title,
.site-name {
  min-width: 0;
}

.site-card:hover {
  box-shadow: 0 0.5rem 1.25rem rgba(16, 24, 40, 0.1) !important;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.2rem 0.6rem;
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1;
}

.status-badge-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.status-badge-green {
  background: #d7f2e3;
  color: #0f7b4d;
}

.status-badge-amber {
  background: #fdf1cc;
  color: #8f6d0a;
}

.status-badge-red {
  background: #fbdfe2;
  color: #b02a37;
}

.status-badge-gray {
  background: #e9ecef;
  color: #6c757d;
}

/* Footer link-outs must never wrap text inside the button.
   (Plain scoped rule: these buttons are this component's own slot content,
   so they carry our scope id — no :deep() needed, which would be unreliable
   through DCard's component root anyway.) */
.footer-actions .btn {
  white-space: nowrap;
  font-size: 0.78rem;
  padding: 0.28rem 0.55rem;
}

/* dot | label | probe summary | cert — columns align across the two rows */
.probe-row {
  display: grid;
  grid-template-columns: 14px 3.2rem 1fr auto;
  gap: 0.5rem;
  align-items: center;
}

.metric {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.status-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  justify-self: center;
}

.history-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
}

.history-dot + .history-dot {
  margin-left: 3px;
}

.history-dot-empty {
  background: transparent;
  border: 1px solid #d6dbe0;
}

.refresh-btn {
  line-height: 1;
  color: var(--bs-primary, #4f46e5);
}

.refresh-btn:disabled {
  opacity: 0.5;
}

.spinning {
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
